import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { Group, GroupFormData } from '../types';
import { useGroupsService } from '../services';
import { useGroupService } from '../services';
import { useGroupFormDialogController } from './GroupFormDialogController';
import { useNavigateWithCompany } from 'src/hooks/useNavigateWithCompany';

export const useGroupsViewController = () => {
  const navigateWithCompany = useNavigateWithCompany();
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const { groupId } = useParams<{ groupId: string }>();
  const previousGroupIdRef = useRef<string | undefined>();

  const {
    loading: groupsLoading,
    filter: groupsFilter,
    list: groupList,
    getGroups,
    changeGroupsFilter,
    resetGroups,
  } = useGroupsService();
  const { archiveGroup: archive, unarchiveGroup: unarchive } = useGroupService();

  const groupFormDialogController = useGroupFormDialogController();

  const initLoad = useCallback(async () => {
    try {
      changeGroupsFilter({ statuses: 'active' });
      await getGroups(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadGroups = useCallback(async () => {
    try {
      await getGroups(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const reloadGroupsDebounce = useMemo(() => debounce(reloadGroups, 500), []);

  const changeGroupFilterForm = useCallback(
    async (filter: Partial<RootState['groups']['filter']>) => {
      changeGroupsFilter(filter);
      await reloadGroups();
    },
    [],
  );

  const changeGroupsSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changeGroupsFilter({ query });
      await reloadGroupsDebounce();
    },
    [],
  );

  const changeGroupsSorting = useCallback(async (s: GridSortModel) => {
    changeGroupsFilter({ sort: `${s[0].field}_${s[0].sort}` });
    await reloadGroups();
  }, []);

  const changeGroupsPagination = useCallback(async (p: GridPaginationModel) => {
    changeGroupsFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadGroups();
  }, []);

  const archiveGroup = useCallback(async (groupId: string) => {
    try {
      await archive(currentCompanyId, groupId);
      await getGroups(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const unarchiveGroup = useCallback(async (groupId: string) => {
    try {
      await unarchive(currentCompanyId, groupId);
      await getGroups(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    initLoad();

    return () => {
      resetGroups();
    };
  }, []);

  // Refresh groups when navigating back from a group view to groups list
  useEffect(() => {
    const previousGroupId = previousGroupIdRef.current;
    previousGroupIdRef.current = groupId;

    // If we were previously viewing a specific group and now we're back to groups list,
    // refresh the groups data to update contact counts
    if (previousGroupId && !groupId && currentCompanyId) {
      reloadGroups();
    }
  }, [groupId, currentCompanyId, reloadGroups]);

  return {
    navigate: navigateWithCompany,
    isPageLoading: groupsLoading.getList,
    groupsFilter,
    groupList,
    reloadGroups,
    changeGroupFilterForm,
    changeGroupsSearchFilter,
    changeGroupsSorting,
    changeGroupsPagination,
    archiveGroup,
    unarchiveGroup,
    groupFormDialogController: {
      ...groupFormDialogController,
      submit: async (full: GroupFormData, dirty: Partial<GroupFormData>) => {
        await groupFormDialogController.submit(full, dirty, async (group: Group) => {
          if (group) {
            // Refresh the groups list first to include the new group
            await getGroups(currentCompanyId);
            // Then navigate to the new group
            navigateWithCompany(`/group/${group.id}`, {
              state: {
                isNew: true,
                openDynamicRules: group.type === 'dynamic',
              },
            });
          } else {
            // For updates, refresh the list
            await getGroups(currentCompanyId);
          }
        });
      },
    },
  };
};
